import type {
    WidgetDataResponse,
    PurchaseResponse,
    SeatMapResponse,
    HoldResponse,
    FeeCalculationResponse,
    FeeCalcItem,
    CartItem,
    AppliedDiscount,
    ContactInfo,
    SelectedSeat,
    SelectedTable,
} from '../types';
import { getCurrencyDisplaySymbol } from '../utils/currencySymbol';
import { estimateSponsorDiscountedCentsForTier } from '../lib/tierCartPricing';
import { buildSponsorDiscountObjectForMetadata, buildSponsorOrderMetadataFromCart } from '../lib/sponsorOrderMetadata';
import { resolveChargeCurrency, getSalesPaymentMethod, isHostedCheckoutRail } from '../lib/paymentRail';

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || 'https://api.meeteor.me';

const WIDGET_VERSION = '0.1.0';

function getOrCreateDeviceId(): string {
    if (typeof sessionStorage === 'undefined') return crypto.randomUUID?.() ?? `widget-${Date.now()}`;
    let id = sessionStorage.getItem('meeteor_widget_device_id');
    if (!id) {
        id = crypto.randomUUID?.() ?? `widget-${Date.now()}`;
        sessionStorage.setItem('meeteor_widget_device_id', id);
    }
    return id;
}

function getDeviceInfo(): string {
    const deviceId = getOrCreateDeviceId();
    const platform = 'web';
    const osVersion =
        typeof navigator !== 'undefined' && navigator.userAgent
            ? navigator.userAgent.slice(0, 80).replace(/[;]/g, '_')
            : 'unknown';
    return `app_name=meeteor;platform=${platform};os_version=${osVersion};app_version=${WIDGET_VERSION};device_id=${deviceId}`;
}

/** Some endpoints return 4xx/5xx with a plain-text body (e.g. rate limits) — never assume JSON. */
function tryParseJsonResponse(raw: string): { kind: 'json'; value: unknown } | { kind: 'text'; text: string } {
    const t = raw.trim();
    if (t === '') return { kind: 'json', value: null };
    try {
        return { kind: 'json', value: JSON.parse(raw) };
    } catch {
        return { kind: 'text', text: raw };
    }
}

function errorMessageFromJsonBody(value: unknown): string | null {
    if (value == null) return null;
    if (typeof value === 'object' && !Array.isArray(value)) {
        const o = value as Record<string, unknown>;
        if (typeof o.message === 'string' && o.message) return o.message;
        if (typeof o.error === 'string' && o.error) return o.error;
    }
    return null;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${API_BASE}${path}`, {
        headers: {
            'Content-Type': 'application/json',
            'X-DEVICE-INFO': getDeviceInfo(),
            'x-app-name': 'meeteor',
            ...options?.headers,
        },
        ...options,
    });
    const rawText = await res.text();
    const parsed = tryParseJsonResponse(rawText);

    if (parsed.kind === 'text') {
        const brief = parsed.text.replace(/\s+/g, ' ').trim().slice(0, 500);
        if (!res.ok) {
            throw new ApiError(brief || 'Request failed', res.status);
        }
        throw new ApiError(
            brief ? `Server returned an unexpected response: ${brief}` : 'Server returned a non-JSON response.',
            res.status,
        );
    }

    const json = parsed.value;
    if (!res.ok) {
        const msg = errorMessageFromJsonBody(json) || 'Request failed';
        throw new ApiError(msg, res.status);
    }
    return json as T;
}

export class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

/**
 * Table row for widget purchase / complete — mirrors `SeatSelectionScreen` + `ICheckoutItem.table_info`
 * (used by backend for “table booking confirmed” and ticket issuance).
 */
export type TableSaleRowInput = {
    table_id: string;
    chairs: number;
    label?: string;
    time_slot?: unknown;
    price?: number;
    fee_tier_id?: string;
    free_tickets_count?: number;
};

function buildTableCheckoutLineItem(t: TableSaleRowInput, itemCurrency: string) {
    const lineCents = t.price != null && Number.isFinite(t.price) ? Math.round(Number(t.price) * 100) : 0;
    const slot = t.time_slot as { day: string; start_time: string; end_time: string } | null | undefined;
    const id =
        slot?.day != null && slot?.start_time != null
            ? `table-${t.table_id}-${slot.day}-${slot.start_time}`
            : `table-${t.table_id}`;
    const freeCount = t.free_tickets_count ?? 0;
    const chairs = t.chairs > 0 ? t.chairs : 1;
    const description = freeCount > 0 ? `${freeCount} Free passes Incl.` : `${chairs} chairs`;
    const tableLabel = t.label || t.table_id;
    return {
        id,
        tierId: t.fee_tier_id as string,
        title: tableLabel,
        description,
        price: lineCents,
        original_price: lineCents,
        currency: itemCurrency,
        quantity: 1,
        category: 'tickets' as const,
        priceInCents: lineCents,
        table_info: {
            table_id: t.table_id,
            table_label: tableLabel,
            chairs,
            ...(slot && {
                time_slot: {
                    day: slot.day,
                    start_time: slot.start_time,
                    end_time: slot.end_time,
                },
            }),
            free_tickets_count: freeCount,
        },
    };
}

/** Cart ticket line in cents — `buildWidgetCartTicketLines` / purchase payloads. */
export type WidgetCartTicketLineCents = {
    id: string;
    tierId: string;
    title: string;
    description: string;
    price: number;
    original_price: number;
    currency: string;
    quantity: number;
    sponsor_discount_code?: string;
    sponsor_discount_id?: string;
    breakdown?: {
        sponsor: NonNullable<CartItem['tier']['sponsor']>;
        sponsor_discount: NonNullable<CartItem['tier']['sponsor_discount']>;
        sponsur_free_tier: boolean;
    };
};

/** Cart line items in cents — `price` after sponsor unlock, `original_price` list (c.tier.fee). Matches `createWidgetPurchase`. */
function buildWidgetCartTicketLines(
    cart: CartItem[],
    currency: string,
    sponsorUnlocks?: Record<string, { value: number; type: string; code: string }>,
): WidgetCartTicketLineCents[] {
    return cart.map((c): WidgetCartTicketLineCents => {
        const unlock = sponsorUnlocks?.[c.tier._id];
        let price = c.tier.fee;
        if (unlock) {
            const isFree = c.tier.sponsur_free_tier || unlock.value >= 100;
            price = isFree
                ? 0
                : estimateSponsorDiscountedCentsForTier(c.tier, {
                      discount: unlock.value,
                      discount_type: unlock.type,
                  });
        }
        const base: WidgetCartTicketLineCents = {
            id: c.tier._id,
            tierId: c.tier._id,
            title: c.tier.fee_name,
            description: c.tier.description || '',
            price,
            original_price: c.tier.fee,
            currency: c.tier.currency || currency,
            quantity: c.quantity,
            ...(unlock && {
                sponsor_discount_code: unlock.code,
                ...(c.tier.sponsor_discount?._id && { sponsor_discount_id: c.tier.sponsor_discount._id }),
            }),
        };
        if (
            unlock &&
            c.tier.sponsor &&
            c.tier.sponsor_discount &&
            !c.tier.sponsur_free_tier
        ) {
            return {
                ...base,
                breakdown: {
                    sponsor: c.tier.sponsor,
                    sponsor_discount: c.tier.sponsor_discount,
                    sponsur_free_tier: !!c.tier.sponsur_free_tier,
                },
            };
        }
        return base;
    });
}

/** meeteor-web `CheckoutModal` /sales — `saleObject.item` uses `name` and prices in major units. */
function toMeeteorWebSaleObjectItemLine(p: {
    id: string;
    tierId: string;
    title: string;
    description?: string;
    priceMajor: number;
    quantity: number;
    currencyUpper: string;
    /** List / face value per unit in major (before sponsor) — matches app `ICheckoutItem` / Clip `original_price` */
    originalPriceMajor?: number;
}) {
    return {
        id: p.id,
        tierId: p.tierId,
        name: p.title,
        price: p.priceMajor,
        quantity: p.quantity,
        description: p.description || '',
        currency: p.currencyUpper,
        original_currency: p.currencyUpper,
        exchange_rate: 1,
        ...(p.originalPriceMajor != null && Number.isFinite(p.originalPriceMajor) && { original_price: p.originalPriceMajor }),
    };
}

export async function fetchWidgetData(widgetId: string, opts?: { eventId?: string }): Promise<WidgetDataResponse> {
    const q = opts?.eventId != null && opts.eventId !== '' ? `?${new URLSearchParams({ event_id: opts.eventId })}` : '';
    return request<WidgetDataResponse>(`/api/v1/widget/${widgetId}${q}`);
}

export async function recordWidgetView(widgetId: string): Promise<void> {
    request(`/api/v1/widget/${widgetId}/view`, { method: 'POST' }).catch(() => {});
}

export type SponsorLeadConsentMeta = {
    event_sponsor_id: string;
    interested: boolean;
};

function applySponsorLeadMetadata(
    metadata: Record<string, unknown>,
    consent?: SponsorLeadConsentMeta,
): void {
    if (!consent?.event_sponsor_id) return;
    metadata.sponsor_lead_consent = consent.interested;
    metadata.sponsor_lead_event_sponsor_id = consent.event_sponsor_id;
}

export async function createWidgetPurchase(
    widgetId: string,
    cart: CartItem[],
    contact: ContactInfo,
    eventId: string,
    currency: string,
    currencyRate: number,
    discount?: AppliedDiscount,
    feeBreakdown?: unknown,
    totalWithFees?: number,
    tableHoldId?: string | null,
    selectedTables?: TableSaleRowInput[] | null,
    sponsorUnlocks?: Record<string, { value: number; type: string; code: string }>,
    refCode?: string,
    viewCorrelationId?: string,
    sponsorAttributions?: Array<{ event_sponsor_id: string }>,
    /** When set, server can resolve the correct sub-event for multi-date / linked events. */
    selectedOccurrenceIso?: string | null,
    paymentRail?: import('../lib/paymentRail').PaymentRail | null,
    sponsorLeadConsent?: SponsorLeadConsentMeta,
): Promise<PurchaseResponse> {
    const eventCurrency = currency;
    const chargeCurrency = resolveChargeCurrency(eventCurrency, paymentRail);
    const cartLineItems = buildWidgetCartTicketLines(cart, eventCurrency, sponsorUnlocks);

    /** Aligned with meetor app `ICheckoutItem` (incl. `table_info` for confirmation email). */
    const tableLineItems: ReturnType<typeof buildTableCheckoutLineItem>[] = [];
    for (const t of selectedTables ?? []) {
        if (!t.fee_tier_id) continue;
        tableLineItems.push(buildTableCheckoutLineItem(t, eventCurrency));
    }

    const items = [...cartLineItems, ...tableLineItems];
    /** Line item `price` values are cents; POST /widget/:id/purchase `amount` must be cents (Stripe). */
    const baseAmountCents = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    const tableListCents = tableLineItems.reduce((s, t) => s + t.original_price * t.quantity, 0);
    /** `totalWithFees` from `calculateFees` / API is in **cents** (same as app fee breakdown). */
    const resolvedTotalCents =
        totalWithFees !== undefined && totalWithFees !== null ? Math.round(Number(totalWithFees)) : baseAmountCents;
    const amount = resolvedTotalCents;
    const totalWithFeesCentsPI = resolvedTotalCents;

    const sponsorCentsMetadata = buildSponsorOrderMetadataFromCart(
        cart,
        sponsorUnlocks,
        baseAmountCents,
        totalWithFeesCentsPI,
        tableListCents,
    );
    const sponsorDiscountMeta = buildSponsorDiscountObjectForMetadata(sponsorUnlocks);
    let sponsorTopLevelDiscountId: string | undefined;
    for (const c of cart) {
        if (sponsorUnlocks?.[c.tier._id] && c.tier.sponsor_discount?._id) {
            sponsorTopLevelDiscountId = c.tier.sponsor_discount._id;
            break;
        }
    }

    const curU = (c: string) => c.toUpperCase();
    const saleObjectItemPI = [
        ...cartLineItems.map(row => {
            const unitMajor = row.price / 100;
            return toMeeteorWebSaleObjectItemLine({
                id: row.id,
                tierId: row.tierId,
                title: row.title,
                description: row.description,
                priceMajor: unitMajor,
                quantity: row.quantity,
                currencyUpper: curU(String(row.currency)),
                originalPriceMajor: row.original_price / 100,
            });
        }),
        ...tableLineItems.map(line => {
            const pMajor = line.price / 100;
            return toMeeteorWebSaleObjectItemLine({
                id: line.id,
                tierId: line.tierId,
                title: line.title,
                description: line.description,
                priceMajor: pMajor,
                quantity: 1,
                currencyUpper: curU(line.currency),
                originalPriceMajor: line.original_price / 100,
            });
        }),
    ];

    const saleObject: Record<string, unknown> = {
        category: 'tickets',
        item: saleObjectItemPI,
        transaction_currency: chargeCurrency,
        linked_event: eventId,
        linked_event_type: 'Event',
        email_used: contact.email,
        exchange_rate: currencyRate,
        max_items: null,
        totalWithFees: totalWithFeesCentsPI,
    };

    if (tableHoldId) saleObject.table_hold_id = tableHoldId;
    /** Omit `table_guests` unless we have additional invitees — buyer must not appear there (boxofix `validateTableGuests`). */
    if (selectedTables?.length) {
        saleObject.selected_tables = selectedTables.map(t => ({
            table_id: t.table_id,
            chairs: t.chairs > 0 ? t.chairs : 1,
            time_slot: t.time_slot ?? null,
            ...(t.label && { label: t.label }),
            ...(t.price != null && { price: t.price }),
        }));
    }
    if (feeBreakdown) saleObject.feeBreakdown = feeBreakdown;
    if (refCode?.trim()) saleObject.referral_code = refCode.trim();

    const metadata: Record<string, unknown> = {
        category: 'tickets',
        items:
            isHostedCheckoutRail(paymentRail)
                ? items.map(line => {
                      const row = line as Record<string, unknown>;
                      const tierId = row.tierId ?? row.id;
                      const normalized: Record<string, unknown> = {
                          ...(tierId != null && { tierId: String(tierId) }),
                          quantity: row.quantity ?? 1,
                          currency: String(row.currency || chargeCurrency).toUpperCase(),
                      };
                      if (typeof row.priceInCents === 'number') {
                          normalized.price = Math.round(row.priceInCents);
                      } else if (typeof row.price === 'number' && tierId == null) {
                          const p = row.price as number;
                          normalized.price = p >= 1000 ? Math.round(p) : Math.round(p * 100);
                      }
                      return normalized;
                  })
                : items,
        linked_event: eventId,
        linked_event_type: 'Event',
        email: contact.email,
        name: contact.name,
        ...(contact.phone?.trim() && { phone: contact.phone.trim() }),
        currencyInfo: {
            transaction_currency: chargeCurrency,
            processing_currency: chargeCurrency,
            original_currency: eventCurrency.toLowerCase(),
            rate: currencyRate,
        },
    };

    if (feeBreakdown) metadata.feeBreakdown = feeBreakdown;
    metadata.totalWithFees = totalWithFeesCentsPI;
    if (refCode?.trim()) metadata.referral_code = refCode.trim();
    if (viewCorrelationId) metadata.correlation_id = viewCorrelationId;
    if (sponsorAttributions && sponsorAttributions.length > 0) {
        metadata.sponsor_attributions = sponsorAttributions;
    }
    applySponsorLeadMetadata(metadata, sponsorLeadConsent);
    if (selectedOccurrenceIso) {
        metadata.selected_occurrence_iso = selectedOccurrenceIso;
    }
    if (Object.keys(sponsorCentsMetadata).length > 0) {
        Object.assign(metadata, sponsorCentsMetadata);
    }
    if (sponsorDiscountMeta && !discount) {
        metadata.discount = sponsorDiscountMeta;
    }
    if (sponsorDiscountMeta?.code && !discount) {
        (metadata as Record<string, unknown>).discount_code = sponsorDiscountMeta.code;
    }

    return request<PurchaseResponse>(`/api/v1/widget/${widgetId}/purchase`, {
        method: 'POST',
        body: JSON.stringify({
            currency: chargeCurrency,
            amount,
            event_id: eventId,
            buyer_email: contact.email,
            buyer_name: contact.name,
            ...(contact.phone?.trim() && { buyer_phone: contact.phone.trim() }),
            items,
            saleObject,
            metadata: JSON.stringify(metadata),
            category: 'tickets',
            ...(paymentRail?.use_flutterwave
                ? { provider: 'flutterwave', callback_url: 'https://meeteor.me/payment/paystack/return' }
                : paymentRail?.use_paystack
                  ? { provider: 'paystack', callback_url: 'https://meeteor.me/payment/paystack/return' }
                  : {}),
            ...(sponsorTopLevelDiscountId && { discountId: sponsorTopLevelDiscountId }),
            ...(discount
                ? { discount: { code: discount.code, type: discount.type, value: discount.value } }
                : sponsorDiscountMeta
                  ? { discount: sponsorDiscountMeta }
                  : {}),
            ...(selectedOccurrenceIso && { selected_occurrence_iso: selectedOccurrenceIso }),
        }),
    });
}

/** Shared shape for hosted verify responses (Paystack | Flutterwave). */
export type HostedVerifyPurchaseResult = {
    paid: boolean;
    reference?: string;
    tx_ref?: string;
    saleId?: string;
    status?: string;
};

/** Confirm widget Paystack checkout after redirect (Step 2 — Paystack). */
export async function verifyPaystackPurchase(reference: string): Promise<HostedVerifyPurchaseResult> {
    const res = await request<{ status: string; data: HostedVerifyPurchaseResult }>(
        '/api/v1/payment/purchase/verify-paystack',
        {
            method: 'POST',
            body: JSON.stringify({ reference }),
        },
    );
    return res.data;
}

/** Confirm widget Flutterwave checkout after redirect. */
export async function verifyFlutterwavePurchase(payload: {
    transaction_id?: string;
    tx_ref?: string;
    reference?: string;
}): Promise<HostedVerifyPurchaseResult> {
    const res = await request<{
        status: string;
        data: HostedVerifyPurchaseResult;
    }>('/api/v1/payment/purchase/verify-flutterwave', {
        method: 'POST',
        body: JSON.stringify({
            transaction_id: payload.transaction_id,
            tx_ref: payload.tx_ref || payload.reference,
            reference: payload.reference || payload.tx_ref,
        }),
    });
    return res.data;
}

/**
 * meeteror-web `CheckoutModal` free table: `POST /ticket` with `table_hold_id`, `selected_tables`,
 * and `items` tier rows. Optional `table_guests` = additional people only (never the buyer’s email).
 */
export type CreateFreeTicketOptions = {
    correlationId?: string;
    tableHoldId?: string | null;
    selectedTables?: TableSaleRowInput[] | null;
    seatHoldId?: string | null;
    selectedSeats?: SelectedSeat[] | null;
    refCode?: string;
    viewCorrelationId?: string;
    sponsorAttributions?: Array<{ event_sponsor_id: string }>;
    /** meeteror-web sends `address: { country, postal_code }` for free table — optional in widget. */
    address?: { country: string; postal_code?: string };
    /** ISO of the user-selected show date (multi-date / linked events). */
    selectedOccurrenceIso?: string | null;
    sponsorLeadConsent?: SponsorLeadConsentMeta;
};

export async function createFreeTicket(
    cart: CartItem[],
    contact: ContactInfo,
    eventId: string,
    options?: CreateFreeTicketOptions,
): Promise<{ data: { saleId: string; message: string } }> {
    const o = options ?? {};
    const fromCart = cart.map(c => ({
        tierId: c.tier._id,
        quantity: c.quantity,
    }));
    const fromTables =
        o.selectedTables?.filter(t => t.fee_tier_id).map(t => ({ tierId: t.fee_tier_id as string, quantity: 1 })) ?? [];
    const items = [...fromCart, ...fromTables];
    if (items.length === 0) {
        throw new ApiError('No tickets or tables to claim', 400);
    }

    const body: Record<string, unknown> = {
        category: 'tickets',
        email: contact.email,
        name: contact.name,
        phone: contact.phone || undefined,
        items,
        linked_event: eventId,
        linked_event_type: 'Event',
        is_authenticated: false,
        ...(o.correlationId && { correlation_id: o.correlationId }),
        ...(o.refCode?.trim() && { referral_code: o.refCode.trim() }),
    };

    if (o.address) {
        body.address = o.address;
    }
    if (o.tableHoldId) {
        body.table_hold_id = o.tableHoldId;
    }
    if (o.selectedTables?.length) {
        body.selected_tables = o.selectedTables.map(t => ({
            table_id: t.table_id,
            chairs: t.chairs > 0 ? t.chairs : 1,
            time_slot: t.time_slot ?? null,
        }));
    }
    if (o.seatHoldId) {
        body.seat_hold_id = o.seatHoldId;
    }
    if (o.selectedSeats?.length) {
        body.selected_seats = o.selectedSeats.map(s => ({
            section_id: s.sectionId,
            row: s.row,
            seat: s.seat,
        }));
    }
    if (o.sponsorAttributions && o.sponsorAttributions.length > 0) {
        body.metadata = {
            sponsor_attributions: o.sponsorAttributions,
            ...(o.viewCorrelationId && { correlation_id: o.viewCorrelationId }),
        };
    } else if (o.viewCorrelationId) {
        body.metadata = { correlation_id: o.viewCorrelationId };
    }
    if (o.sponsorLeadConsent?.event_sponsor_id) {
        const meta =
            body.metadata && typeof body.metadata === 'object'
                ? (body.metadata as Record<string, unknown>)
                : {};
        applySponsorLeadMetadata(meta, o.sponsorLeadConsent);
        body.metadata = meta;
    }
    if (o.selectedOccurrenceIso) {
        if (body.metadata && typeof body.metadata === 'object') {
            (body.metadata as Record<string, unknown>).selected_occurrence_iso = o.selectedOccurrenceIso;
        } else {
            body.metadata = { selected_occurrence_iso: o.selectedOccurrenceIso };
        }
        body.selected_occurrence_iso = o.selectedOccurrenceIso;
    }

    return request<{ data: { saleId: string; message: string } }>('/api/v1/ticket', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/** Seat/table hold fields for POST /sales — same contract as main app `ISaleObject` (CheckoutScreen / processPayment). */
export type CompleteSaleExtras = {
    tableHoldId?: string | null;
    selectedTables?: TableSaleRowInput[] | null;
    seatHoldId?: string | null;
    selectedSeats?: SelectedSeat[] | null;
};

export async function completeSale(
    paymentIntentId: string,
    email: string,
    saleId: string,
    items: CartItem[],
    eventId: string,
    currency: string,
    contact: ContactInfo,
    feeBreakdown?: unknown,
    totalWithFees?: number,
    refCode?: string,
    viewCorrelationId?: string,
    sponsorAttributions?: Array<{ event_sponsor_id: string }>,
    extras?: CompleteSaleExtras,
    selectedOccurrenceIso?: string | null,
    sponsorUnlocks?: Record<string, { value: number; type: string; code: string }>,
    paymentProvider: 'paystack' | 'flutterwave' | 'stripe' = 'stripe',
    /** Stripe US/EU account region from create-purchase / payment rail — required for EU intents. */
    stripeRegion?: string | null,
    sponsorLeadConsent?: SponsorLeadConsentMeta,
): Promise<unknown> {
    if (
        paymentProvider === 'paystack' ||
        paymentProvider === 'flutterwave' ||
        (paymentIntentId && !paymentIntentId.startsWith('pi_'))
    ) {
        throw new Error(
            'Invalid payment confirmation: Stripe /sales requires a PaymentIntent id (pi_…). Hosted checkout must use verify-paystack or verify-flutterwave.',
        );
    }

    const stripeRegionMeta = (() => {
        const region = (stripeRegion || '').toLowerCase();
        if (region === 'us' || region === 'eu' || region) return { stripe_region: region };
        return {};
    })();

    const fromCartCents = buildWidgetCartTicketLines(items, currency, sponsorUnlocks);

    const fromTablesCents =
        extras?.selectedTables
            ?.filter(t => t.fee_tier_id)
            .map(t => buildTableCheckoutLineItem(t, currency.toLowerCase())) ?? [];

    const checkoutItemsCents = [...fromCartCents, ...fromTablesCents];
    const tableListCents = fromTablesCents.reduce((s, t) => s + t.original_price * t.quantity, 0);

    const subtotalCents = checkoutItemsCents.reduce((sum, i) => sum + i.price * i.quantity, 0);
    /** `totalWithFees` from fee calc (Payment) is in **cents**, same as app fee breakdown. */
    const totalWithFeesCents =
        totalWithFees !== undefined && totalWithFees !== null ? Math.round(Number(totalWithFees)) : subtotalCents;

    const sponsorCentsMetadata = buildSponsorOrderMetadataFromCart(
        items,
        sponsorUnlocks,
        subtotalCents,
        totalWithFeesCents,
        tableListCents,
    );
    const sponsorDiscountObj = buildSponsorDiscountObjectForMetadata(sponsorUnlocks);
    let completeSaleSponsorDiscountId: string | undefined;
    for (const c of items) {
        if (sponsorUnlocks?.[c.tier._id] && c.tier.sponsor_discount?._id) {
            completeSaleSponsorDiscountId = c.tier.sponsor_discount._id;
            break;
        }
    }

    /** `/sales` `total` is in major currency units (dollars). */
    const totalMajor = totalWithFeesCents / 100;

    const curUpper = (c: string) => c.toUpperCase();

    type CartLineCents = (typeof fromCartCents)[number];
    const cartRowExtras = (row: CartLineCents) => {
        const r = row as CartLineCents & { sponsor_discount_code?: string; sponsor_discount_id?: string };
        return {
            ...(r.sponsor_discount_code ? { sponsor_discount_code: r.sponsor_discount_code } : {}),
            ...(r.sponsor_discount_id ? { sponsor_discount_id: r.sponsor_discount_id } : {}),
        };
    };

    /** Top-level `items` for POST /sales — major units, same shape as meeteror-web `CheckoutModal`. */
    const itemsForSalesPost: Record<string, unknown>[] = [
        ...fromCartCents.map(row => {
            const cu = curUpper(row.currency || currency);
            return {
                id: row.tierId,
                tierId: row.tierId,
                title: row.title,
                description: row.description,
                price: row.price / 100,
                original_price: row.original_price / 100,
                currency: cu,
                quantity: row.quantity,
                ...cartRowExtras(row),
            };
        }),
        ...fromTablesCents.map(line => {
            const pMajor = line.price / 100;
            const cu = curUpper(line.currency);
            return {
                id: line.id,
                tierId: line.tierId,
                title: line.title,
                description: line.description,
                price: pMajor,
                original_price: pMajor,
                currency: cu,
                quantity: 1,
                category: line.category,
                table_info: line.table_info,
                details: [{ label: 'Chairs', value: String(line.table_info.chairs) }],
                priceInCents: line.price,
            };
        }),
    ];

    const saleObjectItemLines = [
        ...fromCartCents.map(row => {
            const unitMajor = row.price / 100;
            const cu = curUpper(String(row.currency || currency));
            return toMeeteorWebSaleObjectItemLine({
                id: row.tierId,
                tierId: row.tierId,
                title: row.title,
                description: row.description,
                priceMajor: unitMajor,
                quantity: row.quantity,
                currencyUpper: cu,
                originalPriceMajor: row.original_price / 100,
            });
        }),
        ...fromTablesCents.map(line => {
            const pMajor = line.price / 100;
            const cu = curUpper(line.currency);
            return toMeeteorWebSaleObjectItemLine({
                id: line.id,
                tierId: line.tierId,
                title: line.title,
                description: line.description,
                priceMajor: pMajor,
                quantity: 1,
                currencyUpper: cu,
                originalPriceMajor: line.original_price / 100,
            });
        }),
    ];

    const saleObject: Record<string, unknown> = {
        category: 'tickets',
        item: saleObjectItemLines,
        transaction_currency: currency.toLowerCase(),
        linked_event: eventId,
        linked_event_type: 'Event',
        email_used: contact.email,
        exchange_rate: 1,
        max_items: null,
        feeBreakdown: feeBreakdown || undefined,
        totalWithFees: totalWithFeesCents,
        ...(refCode?.trim() && { referral_code: refCode.trim() }),
    };

    if (extras?.tableHoldId) {
        saleObject.table_hold_id = extras.tableHoldId;
    }
    if (extras?.selectedTables?.length) {
        saleObject.selected_tables = extras.selectedTables.map(t => ({
            table_id: t.table_id,
            chairs: t.chairs > 0 ? t.chairs : 1,
            time_slot: t.time_slot ?? null,
            ...(t.label && { label: t.label }),
            ...(t.price != null && { price: t.price }),
        }));
    }
    if (extras?.seatHoldId) {
        saleObject.seat_hold_id = extras.seatHoldId;
    }
    if (extras?.selectedSeats?.length) {
        saleObject.selected_seats = extras.selectedSeats.map(s => ({
            section_id: s.sectionId,
            row: s.row,
            seat: s.seat,
        }));
    }

    const completeMetadata: Record<string, unknown> = {
        email: contact.email,
        name: contact.name,
        ...(contact.phone?.trim() && { phone: contact.phone.trim() }),
        paymentIntent: { id: paymentIntentId },
        currencyInfo: {
            transaction_currency: currency.toUpperCase(),
            processing_currency: currency.toUpperCase(),
            original_currency: currency.toUpperCase(),
            rate: 1,
        },
        feeBreakdown: feeBreakdown || undefined,
        totalWithFees: totalWithFeesCents,
        ...(refCode?.trim() && { referral_code: refCode.trim() }),
        ...(viewCorrelationId && { correlation_id: viewCorrelationId }),
        ...(sponsorAttributions && sponsorAttributions.length > 0 && { sponsor_attributions: sponsorAttributions }),
        ...(selectedOccurrenceIso && { selected_occurrence_iso: selectedOccurrenceIso }),
    };
    applySponsorLeadMetadata(completeMetadata, sponsorLeadConsent);
    if (Object.keys(sponsorCentsMetadata).length > 0) {
        Object.assign(completeMetadata, sponsorCentsMetadata);
    }
    if (sponsorDiscountObj) {
        completeMetadata.discount = sponsorDiscountObj;
    }
    if (sponsorDiscountObj?.code) {
        completeMetadata.discount_code = sponsorDiscountObj.code;
    }

    return request('/api/v1/sales/', {
        method: 'POST',
        body: JSON.stringify({
            category: 'tickets',
            items: itemsForSalesPost,
            total: totalMajor,
            paymentMethod: getSalesPaymentMethod(paymentProvider),
            currency: currency.toUpperCase(),
            metadata: {
                ...completeMetadata,
                payment_provider: paymentProvider,
                ...stripeRegionMeta,
            },
            saleObject,
            ...(completeSaleSponsorDiscountId && { discountId: completeSaleSponsorDiscountId }),
        }),
    });
}

export async function verifyPromoCode(
    eventId: string,
    code: string,
): Promise<{ code: string; value: number; type: 'percentage' | 'fixed' }> {
    const res = await request<{
        status: string;
        data: { code: string; value: number; type: 'percentage' | 'fixed' };
    }>('/api/v1/reedemable-code/discount/verify', {
        method: 'POST',
        body: JSON.stringify({ type: 'event', id: eventId, discount_code: code }),
    });
    return res.data;
}

export async function fetchSeatMap(widgetId: string): Promise<SeatMapResponse> {
    return request<SeatMapResponse>(`/api/v1/widget/${widgetId}/seat-map`);
}

export async function holdSeats(
    widgetId: string,
    seats: SelectedSeat[],
    tables: SelectedTable[],
): Promise<HoldResponse> {
    return request<HoldResponse>(`/api/v1/widget/${widgetId}/hold`, {
        method: 'POST',
        body: JSON.stringify({
            seats: seats.map(s => ({
                section_id: s.sectionId,
                row: s.row,
                seat: s.seat,
            })),
            tables: tables.map(t => ({
                table_id: t.tableId,
                time_slot: t.timeSlot || null,
            })),
        }),
    });
}

export async function releaseHold(widgetId: string, holdId: string): Promise<void> {
    await request(`/api/v1/widget/${widgetId}/hold/${holdId}`, {
        method: 'DELETE',
    });
}

/** Same contract as meeteor-web `lib/tableEventsApi.ts` — public event APIs (no widget prefix). */
export type HoldTablePayload = {
    table_id: string;
    chairs: number;
    time_slot?: { day: string; start_time: string; end_time: string } | null;
};

export async function fetchEventSeatsPublic(eventId: string, feeTierId: string): Promise<any> {
    const q = `?fee_tier_id=${encodeURIComponent(feeTierId)}`;
    return request(`/api/v1/events-seats/${encodeURIComponent(eventId)}/seats${q}`, {
        method: 'GET',
    });
}

export async function postEventTablesHold(
    eventId: string,
    body: {
        tables: HoldTablePayload[];
        fee_tier_id: string;
    },
): Promise<{
    hold_id?: string;
    data?: {
        hold_id?: string;
        pricing?: { total: number; currency: string; fee_tier_id?: string };
        expires_at?: string;
    };
    pricing?: { total: number; currency: string; fee_tier_id?: string };
    expires_at?: string;
    holdId?: string;
}> {
    return request(`/api/v1/events-tables/${encodeURIComponent(eventId)}/tables/hold`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function deleteEventTablesHold(eventId: string, holdId: string): Promise<void> {
    await request(`/api/v1/events-tables/${encodeURIComponent(eventId)}/tables/hold/${encodeURIComponent(holdId)}`, {
        method: 'DELETE',
    });
}

/**
 * POST /mixed/sales-calc — same contract as the meetor app (`ICalculateFees` / `checkout` saga):
 * `amount` is the **subtotal in smallest currency unit (cents)**, not major units.
 */
export async function calculateFees(
    amountCents: number,
    currency: string,
    type: string = 'tickets',
    items?: FeeCalcItem[],
    eventId?: string,
    /** Optional override; default is display symbol (e.g. `$` for USD), not the ISO code. */
    symbol?: string,
): Promise<FeeCalculationResponse> {
    const res = await request<{ status: string; data: FeeCalculationResponse }>('/api/v1/mixed/sales-calc', {
        method: 'POST',
        body: JSON.stringify({
            amount: amountCents,
            currency: currency.toLowerCase(),
            symbol: symbol ?? getCurrencyDisplaySymbol(currency),
            type,
            ...(items && { items }),
            ...(eventId && { eventId }),
        }),
    });
    return (res as any).data ?? res;
}

export async function verifySponsorDiscount(
    eventId: string,
    discountId: string,
): Promise<{ code: string; value: number }> {
    const res = await request<{
        status: string;
        data: { code: string; value: number; message: string };
    }>('/api/v1/reedemable-code/discount/event-verify', {
        method: 'POST',
        body: JSON.stringify({ eventId, discountId }),
    });
    return res.data;
}

export async function createSponsorView(payload: {
    sponsor: string;
    event: string;
    duration_watched: number;
    view_type: 'sponsor';
    device_id?: string;
    correlation_id?: string;
}): Promise<void> {
    const body = {
        ...payload,
        device_id: payload.device_id ?? getOrCreateDeviceId(),
    };
    await request('/api/v1/views', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/** POST /event-sponsors/:id/leads — non-blocking; must not block checkout. */
export async function createSponsorLead(payload: {
    eventSponsorId: string;
    eventId: string;
    interested: boolean;
    correlationId?: string;
    discountId?: string;
    name?: string;
    email?: string;
    phone?: string;
}): Promise<void> {
    const encoded = encodeURIComponent(payload.eventSponsorId);
    await request(`/api/v1/event-sponsors/${encoded}/leads`, {
        method: 'POST',
        body: JSON.stringify({
            event_id: payload.eventId,
            interested: payload.interested,
            device_id: getOrCreateDeviceId(),
            source: 'widget',
            ...(payload.correlationId && { correlation_id: payload.correlationId }),
            ...(payload.discountId && { discount_id: payload.discountId }),
            ...(payload.name?.trim() && { name: payload.name.trim() }),
            ...(payload.email?.trim() && { email: payload.email.trim() }),
            ...(payload.phone?.trim() && { phone: payload.phone.trim() }),
        }),
    });
}

export interface SignupPayload {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    event_role?: string;
    phoneNumber?: string;
    referral_code?: string;
    resendToken?: boolean;
}

/** POST /api/v1/users/signup — same endpoint and payload structure as app (src/redux/authentication/saga.ts signupSaga) */
export async function signup(payload: SignupPayload): Promise<{ message?: string; user?: unknown }> {
    const body: Record<string, unknown> = {
        email: payload.email,
        firstName: payload.firstName,
        lastName: payload.lastName,
        password: payload.password,
        event_role: payload.event_role ?? 'attendee',
        phoneNumber: payload.phoneNumber ?? '',
    };
    if (payload.resendToken === true) body.resendToken = true;
    if (payload.referral_code?.trim()) body.referral_code = payload.referral_code.trim();

    const res = await request<{ status: string; message?: string; user?: unknown }>('/api/v1/users/signup', {
        method: 'POST',
        body: JSON.stringify(body),
    });
    return res;
}

export interface VerifyEmailPayload {
    code: string;
    verificationChannel: string;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    role?: string;
    phoneNumber?: string;
    referral_code?: string;
}

/** POST /api/v1/users/reg-phone-verify — same endpoint and payload structure as app (src/redux/authentication/saga.ts verifyEmailSaga) */
export async function verifyEmail(
    payload: VerifyEmailPayload,
): Promise<{ message?: string; token?: string; user?: unknown }> {
    const body: Record<string, unknown> = {
        code: payload.code,
        verificationChannel: payload.verificationChannel,
        email: payload.email,
        firstName: payload.firstName,
        lastName: payload.lastName,
        password: payload.password,
        event_role: payload.role ?? 'attendee',
        phoneNumber: payload.phoneNumber ?? '',
    };
    if (payload.referral_code?.trim()) body.referral_code = payload.referral_code.trim();

    const res = await request<{ status: string; message?: string; token?: string; user?: unknown }>(
        '/api/v1/users/reg-phone-verify',
        {
            method: 'POST',
            body: JSON.stringify(body),
        },
    );
    return res;
}
